import { useState, useEffect, useCallback } from "react";

const usePersistedSelection = (storageKey: string) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => {
    try {
      const storedSelection = localStorage.getItem(storageKey);
      if (storedSelection) {
        const parsedSelection = JSON.parse(storedSelection);
        return new Set(parsedSelection);
      }
    } catch (error) {
      console.warn("Failed to parse stored selection:", error);
    }
    return new Set();
  });

  useEffect(() => {
    try {
      const selectionArray = Array.from(selectedItems);
      localStorage.setItem(storageKey, JSON.stringify(selectionArray));
    } catch (error) {
      console.warn("Failed to persist selection:", error);
    }
  }, [selectedItems, storageKey]);

  const updateSelection = useCallback((newSelection: Set<string>) => {
    setSelectedItems(newSelection);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const addItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => new Set(Array.from(prev).concat(itemId)));
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  const toggleItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  return {
    selectedItems,
    updateSelection,
    clearSelection,
    addItem,
    removeItem,
    toggleItem,
    selectedCount: selectedItems.size,
  };
};

export default usePersistedSelection;
